<template>
  <CardRoot>
    <CardBody>
      <div class="mb-4 d-flex justify-content-between align-items-start">
        <div>
          <CardTitle>
            {{ $t("dashboard.consolidated.production_devices") }}
          </CardTitle>
          <CardDescription>
            {{
              $t("dashboard.consolidated.production_devices_description", {
                deviceCount,
                companyCount,
              })
            }}
          </CardDescription>
        </div>
        <div class="d-flex gap-2 flex-shrink-0">
          <IssuanceChartButton :companyIds="companyIds" />
          <button
            v-if="companyCount > 1"
            type="button"
            class="btn btn-sm btn-outline-primary flex-shrink-0"
            @click="breakdownModal?.toggleModal()"
          >
            <font-awesome-icon icon="table-columns" class="me-1" />
            {{
              $t(
                "dashboard.consolidated.production_devices_table.view_breakdown"
              )
            }}
          </button>
        </div>
      </div>

      <ProductionDevicesTable
        :companies="companies"
        :registryDevices="registryDevices"
      />
    </CardBody>
  </CardRoot>

  <ModalComponent
    ref="breakdownModal"
    :submitButtonText="$t('common.close')"
    :hideSubmitButton="true"
    :noFooter="true"
    size="lg"
  >
    <template #title>
      {{ $t("dashboard.consolidated.production_devices_table.view_breakdown") }}
    </template>
    <template #body>
      <div class="table-responsive pb-3">
        <table class="table table-sm align-middle mb-0">
          <thead class="text-muted">
            <tr>
              <th>
                {{
                  $t(
                    "dashboard.consolidated.production_devices_table.company_name"
                  )
                }}
              </th>
              <th class="text-end">
                {{
                  `${$t("dashboard.consolidated.production_devices_table.capacity")} (${$t("common.kw")})`
                }}
              </th>
              <th class="text-end">
                {{
                  $t(
                    "dashboard.consolidated.production_devices_table.device_count"
                  )
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="summary in companySummaries" :key="summary.id">
              <td class="fw-semibold">{{ summary.name }}</td>
              <td class="text-end font-variant-numeric-tabular">
                {{ summary.totalPower.toLocaleString() }}
              </td>
              <td class="text-end">{{ summary.deviceCount }}</td>
            </tr>
          </tbody>
          <tfoot class="border-top">
            <tr class="fw-bold">
              <td>
                {{
                  $t("dashboard.consolidated.production_devices_table.total")
                }}
              </td>
              <td class="text-end font-variant-numeric-tabular">
                {{ totalPower.toLocaleString() }}
              </td>
              <td class="text-end">{{ deviceCount }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import {
  CardBody,
  CardDescription,
  CardRoot,
  CardTitle,
} from "@/components/common/card";
import { Company, RegistryDevice } from "@/types";
import { computed, useTemplateRef } from "vue";
import IssuanceChartButton from "./IssuanceChartButton.vue";
import ProductionDevicesTable from "./ProductionDevicesTable.vue";

const props = defineProps<{
  companies: (Company & { id: number })[];
  registryDevices: RegistryDevice[];
}>();

const breakdownModal =
  useTemplateRef<InstanceType<typeof ModalComponent>>("breakdownModal");

const deviceCount = computed(() => (props.registryDevices ?? []).length);
const companyCount = computed(() => (props.companies ?? []).length);
const companyIds = computed(() => (props.companies ?? []).map((c) => c.id));
const totalPower = computed(() =>
  (props.registryDevices ?? []).reduce(
    (sum, d) => sum + (d.device_power ?? 0),
    0
  )
);

const companySummaries = computed(() =>
  props.companies
    .map((company) => {
      const devices = props.registryDevices.filter(
        (d) => d.company_id === company.id
      );
      return {
        id: company.id,
        name: company.name ?? "",
        deviceCount: devices.length,
        totalPower: devices.reduce((sum, d) => sum + (d.device_power ?? 0), 0),
      };
    })
    .sort((a, b) => b.totalPower - a.totalPower)
);
</script>

<style scoped lang="scss">
.font-variant-numeric-tabular {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
</style>
